/* =====================================================================
   Browns Digital Studio — data layer (v4)
   Supabase in live mode, localStorage in demo mode. Hosted publishing:
   staging → promote → production subdomain. No customer code export —
   buildSiteFiles output goes to the server (or, in demo, to memory).
===================================================================== */
"use strict";

/* Keep in sync with BD_PUBLISH_RUNTIME in api/publish.php. */
var BD_PUBLISH_RUNTIME = "2026-10-23.1";

let DEMO = true;
let sb = null;
let sbInitKey = "";
let _bdLiveConfig = false;
let _bdInitReason = "";

function _bdTokenMark(token){
  let h = 2166136261;
  for (let i = 0; i < token.length; i++){
    h ^= token.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}
/* Local note of the last session.php POST. The SSO cookie is HttpOnly, so this
   is how boot knows it is already fresh and can skip another round trip. */
function _bdPlatformCookieFresh(session){
  if (!session?.access_token) return false;
  try {
    const row = JSON.parse(localStorage.getItem("bd_platform_cookie_v1") || "null");
    if (!row || row.mark !== _bdTokenMark(session.access_token)) return false;
    const exp = Number(row.expires_at) || 0;
    if (exp > 0 && exp < Math.floor(Date.now() / 1000) + 60) return false;
    return true;
  } catch { return false; }
}
function _bdRememberPlatformCookie(session){
  if (!session?.access_token) return;
  try {
    localStorage.setItem("bd_platform_cookie_v1", JSON.stringify({
      mark: _bdTokenMark(session.access_token),
      expires_at: Number(session.expires_at) || 0
    }));
  } catch {}
}
function _bdForgetPlatformCookie(){
  try { localStorage.removeItem("bd_platform_cookie_v1"); } catch {}
}
async function _bdPlatformAuth(op, client){
  if (DEMO || !client || typeof BD_AUTH === "undefined") return;
  try {
    if (op === "establish") {
      const ok = await BD_AUTH.establish(client);
      if (ok) {
        const { data } = await client.auth.getSession();
        _bdRememberPlatformCookie(data?.session);
      }
      return ok;
    }
    if (op === "clear") {
      _bdForgetPlatformCookie();
      await BD_AUTH.clear();
    } else if (op === "hydrate") await BD_AUTH.hydrate(client);
  } catch (_) {}
}
/* POST the platform cookie only when this browser has not stored the current
   token, or that token is inside the 60s refresh window. Never blocks drawing. */
function _bdEnsurePlatformCookie(client){
  if (DEMO || !client || typeof BD_AUTH === "undefined") return;
  client.auth.getSession().then(({ data }) => {
    const session = data?.session;
    if (!session?.access_token || _bdPlatformCookieFresh(session)) return;
    return _bdPlatformAuth("establish", client);
  }).catch(() => {});
}

function _bdOAuthRedirectUrl(){
  const url = new URL(location.href);
  ["code", "error", "error_code", "error_description"].forEach(key => url.searchParams.delete(key));
  url.hash = "";
  return url.toString();
}

function _bdCleanOAuthUrl(){
  const url = new URL(location.href);
  ["code", "error", "error_code", "error_description"].forEach(key => url.searchParams.delete(key));
  url.hash = "";
  history.replaceState(null, "", url.pathname + url.search);
}

async function bdInitApi(){
  const live = await bdLoadConfig();
  _bdLiveConfig = !!live;
  _bdInitReason = "";
  DEMO = !live;
  if (!live){
    sb = null;
    sbInitKey = "";
    const prodHost = /^(www\.)?studio\.brownsdigital\.org$/i.test(location.hostname);
    if (prodHost) {
      _showCfgBanner("Studio is not configured on this server — contact support@brownsdigital.org.");
      DEMO = false;
      _bdInitReason = "Config values are missing on this host.";
    } else if (typeof supabase === "undefined"){
      _showCfgBanner("Studio couldn't load. Check your connection or ad blocker.");
      DEMO = true;
      _bdInitReason = "The app library did not load.";
    } else {
      document.getElementById("demo-banner")?.classList.add("show");
      _bdInitReason = "Running in demo mode (local fallback).";
    }
  } else if (typeof supabase === "undefined"){
    _showCfgBanner("Studio couldn't load. Check your connection or ad blocker.");
    DEMO = true;
    sb = null;
    sbInitKey = "";
    _bdInitReason = "The app library did not load.";
  } else {
    try {
      const key = SUPABASE_URL + "\0" + SUPABASE_ANON_KEY;
      if (!sb || sbInitKey !== key) {
        sb = supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
          auth: {
            experimental: { passkey: true }
          },
          global: {
            /* Auth and profile stay on the short cap. A site body can be
               several megabytes of uploaded images, so project reads and
               writes get their own limits. */
            fetch: (url, opts) => {
              const href = typeof url === "string" ? url : (url && url.url) || "";
              const method = String((opts && opts.method) || (typeof url !== "string" && url && url.method) || "GET").toUpperCase();
              let ms = 8000;
              if (/\/rest\/v1\/projects(?:\?|$)/.test(href)) {
                ms = (method === "GET" || method === "HEAD") ? 20000 : 60000;
              }
              return (globalThis.bdFetch || fetch)(url, opts, ms);
            }
          }
        });
        sbInitKey = key;
      }
    } catch (e) {
      _showCfgBanner("Studio couldn't load. Check your connection or ad blocker.");
      console.error(e);
      DEMO = true;
      sb = null;
      sbInitKey = "";
      _bdInitReason = "The app could not start from the server configuration.";
    }
  }
  if (!DEMO && !sb && _bdInitReason === "") _bdInitReason = "The app client is unavailable.";
  if (DEMO && _bdInitReason === "") _bdInitReason = "Running in demo mode.";
  if (DEMO) document.getElementById("demo-banner")?.classList.add("show");
  else document.getElementById("demo-banner")?.classList.remove("show");
  return !DEMO;
}

function mfaLiveError(feature){
  return new Error(feature + " needs a live connection. Check your connection or ad blocker.");
}

/** Authenticator apps show issuer from otpauth; brand only — never Site URL or email. */
function brandTotpAuthUri(secret, existingUri, brand = "Browns Digital"){
  const sec = String(secret || "").replace(/\s+/g, "");
  if (!sec) return "";
  let algo = "SHA1", digits = "6", period = "30";
  try {
    const u = new URL(String(existingUri || ""));
    algo = u.searchParams.get("algorithm") || algo;
    digits = u.searchParams.get("digits") || digits;
    period = u.searchParams.get("period") || period;
  } catch (_) { /* keep defaults */ }
  // Label + issuer are both the brand so apps show "Browns Digital", not host:email.
  const label = encodeURIComponent(brand);
  const iss = encodeURIComponent(brand);
  return `otpauth://totp/${label}?secret=${encodeURIComponent(sec)}&issuer=${iss}&algorithm=${encodeURIComponent(algo)}&digits=${encodeURIComponent(digits)}&period=${encodeURIComponent(period)}`;
}
let _bdQrLoad = null;
function loadQrCode(){
  if (typeof QRCode === "function") return Promise.resolve();
  if (_bdQrLoad) return _bdQrLoad;
  _bdQrLoad = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.crossOrigin = "anonymous";
    s.integrity = "sha384-3zSEDfvllQohrq0PHL1fOXJuC/jSOO34H46t6UQfobFOmxE5BpjjaIJY5F2/bMnU";
    s.src = "https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load the QR code."));
    document.head.appendChild(s);
  });
  return _bdQrLoad;
}
function brandTotpQrDataUrl(uri){
  if (!uri || typeof QRCode !== "function") return "";
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-9999px;top:0;width:220px;height:220px;opacity:0;pointer-events:none";
  try {
    document.body.appendChild(host);
    const opts = {
      text: uri,
      width: 220,
      height: 220,
      colorDark: "#1d1d1f",
      colorLight: "#ffffff"
    };
    if (QRCode.CorrectLevel && QRCode.CorrectLevel.M != null) opts.correctLevel = QRCode.CorrectLevel.M;
    new QRCode(host, opts);
    const canvas = host.querySelector("canvas");
    if (canvas && typeof canvas.toDataURL === "function") return canvas.toDataURL("image/png");
    const img = host.querySelector("img");
    return (img && img.src) ? img.src : "";
  } catch (_) {
    return "";
  } finally {
    try { host.remove(); } catch (_) { /* ignore */ }
  }
}

function passkeyBytes(value){
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  const s = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(s + "=".repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(raw, ch => ch.charCodeAt(0));
}
function passkeyBase64Url(value){
  const bytes = passkeyBytes(value);
  let raw = "";
  bytes.forEach(byte => { raw += String.fromCharCode(byte); });
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function passkeyIsCancelErr(e){
  const name = String(e?.name || e?.code || "");
  const msg = String(e?.message || e || "");
  return /AbortError|NotAllowedError|ERROR_CEREMONY_ABORTED|ERROR_PASSTHROUGH/i.test(name)
    || /not allowed|abort|cancel|timed? ?out|empty credential|ceremony was sent an abort/i.test(msg);
}
function passkeyCreationOptions(serverOptions){
  let json = typeof structuredClone === "function"
    ? structuredClone(serverOptions)
    : JSON.parse(JSON.stringify(serverOptions));
  if (json?.publicKey && (json.publicKey.challenge || json.publicKey.user)) json = json.publicKey;
  /* Match @supabase/auth-js deserializeCredentialCreationOptions — no field mutations. */
  if (typeof PublicKeyCredential !== "undefined" && typeof PublicKeyCredential.parseCreationOptionsFromJSON === "function"){
    return PublicKeyCredential.parseCreationOptionsFromJSON(json);
  }
  const options = {
    ...json,
    challenge: passkeyBytes(json.challenge).buffer,
    user: { ...json.user, id: passkeyBytes(json.user.id).buffer },
  };
  if (Array.isArray(json.excludeCredentials)){
    options.excludeCredentials = json.excludeCredentials.map(item => ({
      ...item,
      id: passkeyBytes(item.id).buffer,
      type: item.type || "public-key",
    }));
  }
  return options;
}
/* Prefer browser toJSON when it returns strings; otherwise encode like auth-js. */
function passkeyRegistrationJson(credential){
  if (typeof credential?.toJSON === "function"){
    try {
      const json = credential.toJSON();
      if (json && typeof json.id === "string" && typeof json.response?.clientDataJSON === "string"
          && typeof json.response?.attestationObject === "string"){
        if (json.response.transports == null) delete json.response.transports;
        return json;
      }
    } catch {}
  }
  return {
    id: credential.id,
    rawId: credential.id,
    type: "public-key",
    response: {
      attestationObject: passkeyBase64Url(credential.response.attestationObject),
      clientDataJSON: passkeyBase64Url(credential.response.clientDataJSON),
    },
    clientExtensionResults: typeof credential.getClientExtensionResults === "function"
      ? credential.getClientExtensionResults()
      : {},
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
  };
}
function passkeyClientMeta(credential, serverOptions){
  try {
    const raw = credential?.response?.clientDataJSON;
    const text = typeof raw === "string"
      ? atob(String(raw).replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (String(raw).length % 4)) % 4))
      : new TextDecoder().decode(raw);
    const clientData = JSON.parse(text);
    return {
      clientOrigin: clientData.origin || "",
      pageOrigin: typeof location !== "undefined" ? location.origin : "",
      rpId: serverOptions?.rp?.id || serverOptions?.rpId || "",
      type: clientData.type || "",
    };
  } catch {
    return {
      clientOrigin: "",
      pageOrigin: typeof location !== "undefined" ? location.origin : "",
      rpId: serverOptions?.rp?.id || "",
      type: "",
    };
  }
}

function _showCfgBanner(msg){
  const el = document.getElementById("demo-banner");
  if (!el) return;
  el.classList.add("show", "cfg-err");
  el.querySelector(".db-pill").innerHTML = `<i></i>${msg}`;
}

const ls = {
  get(k, d){ try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } },
  set(k, v){ localStorage.setItem(k, JSON.stringify(v)); },
  del(k){ localStorage.removeItem(k); }
};

async function demoPassHash(password){
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(String(password)));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
}
async function demoPassOk(user, password){
  if (!user) return false;
  if (user.passHash) return user.passHash === await demoPassHash(password);
  return user.password === password;
}
async function demoSetPass(user, password){
  user.passHash = await demoPassHash(password);
  delete user.password;
}

function deployErrorFallback(status){
  if (status === 401) return "Your session expired — sign in again.";
  if (status === 403) return "Your plan doesn't allow this action. See brownsdigital.org/#pricing.";
  if (status === 409) return "That conflicts with another site or address on your account.";
  if (status === 413) return "This deploy is too large — try fewer pages or smaller images.";
  if (status === 429) return "Too many publishes in a short time — wait a bit and try again.";
  if (status === 503) return "Publishing isn't available right now. Contact support@brownsdigital.org.";
  if (status >= 500) return "Something went wrong on our end. Please try again in a moment.";
  return "Publish failed — check your connection and try again.";
}

/* One profiles row read for avatar + prefs — avoids parallel Supabase calls that
   can trip Cloudflare 522s and surface as bogus CORS errors in the browser. */
let _profileBundle = null;
let _profileBundleInflight = null;

function _bdBundleCacheRead(uid){
  try {
    const row = JSON.parse(sessionStorage.getItem("bd_profile_bundle_v3") || "null");
    if (!row || row.uid !== uid || !row.v || (Date.now() - row.t) > 300000) return null;
    return row.v;
  } catch { return null; }
}
function _bdBundleCacheWrite(uid, value){
  try { sessionStorage.setItem("bd_profile_bundle_v3", JSON.stringify({ t: Date.now(), uid, v: value })); } catch {}
}
function _bdInvalidateProfileBundle(){
  _profileBundle = null;
  try { sessionStorage.removeItem("bd_profile_bundle_v3"); } catch {}
}

/** Google/GitHub photo from Supabase auth user (metadata + identity_data). */
function _bdAvatarFromAuthUser(u){
  if (!u) return "";
  const pick = (...vals) => {
    for (const v of vals){
      const s = String(v || "").trim();
      if (/^https?:\/\//i.test(s) || s.startsWith("data:image/")) return s;
    }
    return "";
  };
  const meta = u.user_metadata || {};
  let avatar = pick(meta.avatar_url, meta.picture, meta.avatar);
  if (avatar) return avatar;
  const ids = Array.isArray(u.identities) ? u.identities : [];
  for (const id of ids){
    const d = id?.identity_data || {};
    avatar = pick(d.avatar_url, d.picture, d.avatar);
    if (avatar) return avatar;
  }
  return "";
}

function _bdOAuthProviderFromAuthUser(u){
  if (!u) return "";
  const app = String(u.app_metadata?.provider || "").toLowerCase();
  if (app === "google" || app === "github") return app;
  const ids = Array.isArray(u.identities) ? u.identities : [];
  for (const id of ids){
    const p = String(id?.provider || "").toLowerCase();
    if (p === "google" || p === "github") return p;
  }
  return "";
}

function _bdTransientProfileErr(err){
  const code = err?.status ?? err?.code;
  const msg = String(err?.message || err || "");
  return code === 522 || /522|cloudflare|timeout|failed to fetch|network/i.test(msg);
}

const _bdProfileCols = ["plan", "suspended", "is_admin", "qa_access", "avatar_url", "full_name", "builder_prefs", "theme_prefs", "account_prefs"];

function _bdProfileSkipCols(){
  try {
    const row = JSON.parse(localStorage.getItem("bd_profile_skip_cols") || "null");
    if (!row || !Array.isArray(row.cols) || (Date.now() - row.t) > 86400000) return [];
    return row.cols.filter(c => _bdProfileCols.includes(c) && c !== "plan" && c !== "is_admin");
  } catch { return []; }
}
function _bdProfileColumns(){
  const skip = new Set(_bdProfileSkipCols());
  return _bdProfileCols.filter(c => !skip.has(c));
}
function _bdNoteMissingProfileCols(err){
  const msg = String(err?.message || err || "");
  if (!/column|schema cache/i.test(msg)) return false;
  const named = _bdProfileCols.filter(c => c !== "plan" && c !== "is_admin" && msg.includes(c));
  if (!named.length) return false;
  const cols = Array.from(new Set(_bdProfileSkipCols().concat(named)));
  try { localStorage.setItem("bd_profile_skip_cols", JSON.stringify({ t: Date.now(), cols })); } catch {}
  return true;
}

async function _bdFetchProfileBundleLive(){
  if (DEMO || !sb) return {};
  if (_profileBundle) return _profileBundle;
  if (_profileBundleInflight) return _profileBundleInflight;
  _profileBundleInflight = (async () => {
    try {
      const { data: s } = await sb.auth.getSession();
      const uid = s?.session?.user?.id;
      if (!uid) return {};
      const cached = _bdBundleCacheRead(uid);
      if (cached) { _profileBundle = cached; return cached; }
      const query = (cols) => sb.from("profiles").select(cols.join(",")).eq("id", uid).single();
      let cols = _bdProfileColumns();
      let res = await query(cols);
      if (res.error && _bdNoteMissingProfileCols(res.error)) {
        const next = _bdProfileColumns();
        if (next.join(",") !== cols.join(",")) {
          cols = next;
          res = await query(cols);
        }
      }
      if (res.error && _bdTransientProfileErr(res.error)) {
        res = await query(_bdProfileColumns());
      }
      if (!res.error) {
        _profileBundle = res.data || {};
        _bdBundleCacheWrite(uid, _profileBundle);
        return _profileBundle;
      }
      console.warn("[bd] profile bundle fetch failed", res.error);
      return _profileBundle || {};
    } finally {
      _profileBundleInflight = null;
    }
  })();
  return _profileBundleInflight;
}

const api = {
  /* ------------------------------ auth ------------------------------ */
  async signUp(name, email, password){
    if (QA_MODE) throw new Error("Studio is invite-only during QA. Ask your QA lead for an account.");
    if (DEMO){
      const users = ls.get("bd_users", {});
      if (users[email]) throw new Error("An account with that email already exists.");
      users[email] = { name, plan: "studio", verified: true, qa_access: false };
      await demoSetPass(users[email], password);
      ls.set("bd_users", users);
      ls.set("bd_session", { email });
      return { confirmed: true, email };
    }
    const { data, error } = await sb.auth.signUp({
      email, password,
      options: {
        data: {
          full_name: name,
          username: String(name || "").toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 32)
        },
        emailRedirectTo: location.origin + location.pathname
      }
    });
    if (error) throw error;
    return { confirmed: !!data.session, email };
  },
  async resendVerification(email){
    email = String(email || "").trim().toLowerCase();
    if (DEMO){
      const u = ls.get("bd_users", {})[email];
      if (!u) throw new Error("No account found with that email.");
      if (u.verified) throw new Error("This email is already verified.");
      const code = String(Math.floor(100000 + Math.random() * 900000));
      ls.set("bd_verify_" + email, { code, name: u.name, at: Date.now() });
      return { demoCode: code };
    }
    const { error } = await sb.auth.resend({ type: "signup", email, options: { emailRedirectTo: location.origin + location.pathname } });
    if (error) throw error;
    return {};
  },
  async verifyEmailCode(email, code){
    email = String(email || "").trim().toLowerCase();
    if (DEMO){
      const pending = ls.get("bd_verify_" + email, null);
      const users = ls.get("bd_users", {});
      if (!users[email]) throw new Error("No account found with that email.");
      if (users[email].verified) return {};
      if (!pending || pending.code !== String(code || "").trim()) throw new Error("That code doesn't match. Check your email or send a new code.");
      users[email].verified = true;
      ls.set("bd_users", users);
      ls.del("bd_verify_" + email);
      return {};
    }
    throw new Error("Open the link in your email to verify your account.");
  },
  async isEmailVerified(email){
    if (DEMO){
      const u = ls.get("bd_users", {})[email];
      return !u || !!u.verified;
    }
    const { data } = await sb.auth.getUser();
    return !!data.user?.email_confirmed_at;
  },
  async signIn(email, password){
    if (DEMO){
      const u = ls.get("bd_users", {})[email];
      if (!(await demoPassOk(u, password))) {
        const err = new Error("That email and password don’t match an account.");
        err.credentialMismatch = true;
        throw err;
      }
      if (u.password) { await demoSetPass(u, password); ls.set("bd_users", ls.get("bd_users", {})); }
      if (u.verified === false) throw new Error("Verify your email before signing in. Check your inbox for the confirmation link.");
      ls.set("bd_session", { email });
      return;
    }
    const { data, error } = await sb.auth.signInWithPassword({ email, password });
    if (error){
      if (/invalid login credentials/i.test(String(error.message || ""))) {
        const err = new Error("That email and password don't match. If you signed up with GitHub or another social provider, use that sign-in option instead.");
        err.credentialMismatch = true;
        throw err;
      }
      throw error;
    }
    if (!data.user?.email_confirmed_at) {
      await sb.auth.signOut();
      throw new Error("Verify your email before signing in. Check your inbox for the confirmation link.");
    }
    await _bdPlatformAuth("establish", sb);
  },
  async signInWithPasskey(){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("Passkey sign-in");
    if (!sb?.auth?.signInWithPasskey) throw new Error("Passkey sign-in is unavailable right now.");
    const { data, error } = await sb.auth.signInWithPasskey();
    if (error) {
      const detail = String(error.message || error.code || error.error_code || "");
      if (/webauthn|verification|origin|rp.?id|relying.?party/i.test(detail)) {
        const err = new Error("Passkey sign-in didn’t complete. Try again, or continue with Google or GitHub.");
        err.cause = error;
        throw err;
      }
      throw error;
    }
    if (!data?.user?.email_confirmed_at) {
      await sb.auth.signOut();
      throw new Error("Verify your email before signing in. Check your inbox for the confirmation link.");
    }
    await _bdPlatformAuth("establish", sb);
    return data;
  },
  async signInWithGitHub(){
    if (QA_MODE) throw new Error("Studio is invite-only during QA. Ask your QA lead for an account.");
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("GitHub sign-in");
    const { error } = await sb.auth.signInWithOAuth({
      provider: "github",
      options: { redirectTo: _bdOAuthRedirectUrl() }
    });
    if (error) throw error;
  },
  async signInWithGoogle(){
    if (QA_MODE) throw new Error("Studio is invite-only during QA. Ask your QA lead for an account.");
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("Google sign-in");
    const { error } = await sb.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: _bdOAuthRedirectUrl(),
        queryParams: { prompt: "select_account" }
      }
    });
    if (error) throw error;
  },
  async signOut(){
    if (DEMO){ ls.del("bd_session"); return; }
    await _bdPlatformAuth("clear", sb);
    await sb.auth.signOut();
  },
  async handleAuthRedirect(){
    if (DEMO || !sb) return { handled: false, recovery: false, verified: false };
    const hash = location.hash || "";
    const code = new URLSearchParams(location.search).get("code");
    const fromHash = /access_token|type=signup|type=recovery|type=email/.test(hash);
    /* Supabase detectSessionInUrl may already have consumed the PKCE code
       before we run — still push the platform SSO cookie if a session exists. */
    if (!fromHash && !code){
      _bdEnsurePlatformCookie(sb);
      return { handled: false, recovery: false, verified: false };
    }
    const verified = /type=signup|type=email/.test(hash);
    if (code){
      const { data, error } = await sb.auth.exchangeCodeForSession(code);
      if (error){
        /* Code may already be spent by client init — fall through to session sync */
        const { data: existing } = await sb.auth.getSession();
        if (!existing?.session) throw error;
        _bdCleanOAuthUrl();
        _bdEnsurePlatformCookie(sb);
        return { handled: true, recovery: /type=recovery/.test(hash), verified };
      }
      const recovery = /type=recovery/.test(hash)
        || (typeof BD_AUTH !== "undefined" && BD_AUTH.sessionUsedRecovery(data.session));
      _bdCleanOAuthUrl();
      _bdEnsurePlatformCookie(sb);
      return { handled: true, recovery, verified };
    }
    await sb.auth.getSession();
    _bdEnsurePlatformCookie(sb);
    _bdCleanOAuthUrl();
    return { handled: true, recovery: /type=recovery/.test(hash), verified };
  },
  async session(){
    if (DEMO){
      const s = ls.get("bd_session", null); if (!s) return null;
      const u = ls.get("bd_users", {})[s.email];
      if (!u) return null;
      let avatar = "";
      try { avatar = localStorage.getItem("bd_avatar_" + s.email) || ""; } catch {}
      return { email: s.email, name: u.name || "", username: u.username || "", id: u.id || s.email, avatar };
    }
    const { data } = await sb.auth.getSession();
    if (!data.session && sb) await _bdPlatformAuth("hydrate", sb);
    const after = await sb.auth.getSession();
    const sess = after.data.session;
    if (!sess) return null;
    const u = sess.user;
    const meta = u.user_metadata || {};
    return {
      email: u.email,
      name: meta.full_name || meta.name || "",
      username: String(meta.username || "").replace(/[^a-z0-9_]/gi, "").slice(0, 32),
      id: u.id,
      avatar: _bdAvatarFromAuthUser(u),
      oauthProvider: _bdOAuthProviderFromAuthUser(u)
    };
  },
  async accessToken(){
    if (DEMO) return "demo";
    const { data } = await sb.auth.getSession();
    return data.session?.access_token || null;
  },
  async plan(){
    if (DEMO){
      const s = ls.get("bd_session", null);
      return s ? (ls.get("bd_users", {})[s.email]?.plan || "free") : "free";
    }
    const data = await _bdFetchProfileBundleLive();
    if (data && Object.prototype.hasOwnProperty.call(data, "plan")) {
      return String(data.plan || "free").toLowerCase();
    }
    return "free";
  },
  /* Platform admin check. Live: profiles.is_admin only (server enforces too). */
  async isAdmin(email){
    if (DEMO) return !!ls.get("bd_users", {})[email]?.is_admin;
    const data = await _bdFetchProfileBundleLive();
    if (data && Object.prototype.hasOwnProperty.call(data, "is_admin")) return !!data.is_admin;
    return false;
  },
  isRecovery(){
    if (DEMO) return false;
    if (typeof BD_AUTH !== "undefined" && BD_AUTH.recoveryFromUrl) return BD_AUTH.recoveryFromUrl();
    const hash = location.hash || "";
    const q = new URLSearchParams(location.search);
    return /type=recovery/.test(hash) || q.get("type") === "recovery";
  },
  async isRecoverySession(){
    if (DEMO || !sb) return false;
    if (typeof BD_AUTH !== "undefined" && BD_AUTH.isRecoverySession) return BD_AUTH.isRecoverySession(sb);
    return this.isRecovery();
  },
  async requestReset(email){
    email = String(email || "").trim().toLowerCase();
    if (!email) throw new Error("Enter your account email.");
    if (DEMO) throw new Error("Password reset is not available in demo mode.");
    if (!_bdLiveConfig) throw mfaLiveError("Password reset");
    const res = await bdFetch(API_BASE + "auth.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "reset_password", email })
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || "Could not send the reset email.");
    return {};
  },
  async accountExists(email){
    email = String(email || "").trim().toLowerCase();
    if (!email) return null;
    if (DEMO) return !!ls.get("bd_users", {})[email];
    await bdInitApi();
    if (!_bdLiveConfig) return null;
    const res = await bdFetch(API_BASE + "auth.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "account_exists", email })
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) return null;
    return !!out.exists;
  },
  async demoReset(email, newPass){
    const users = ls.get("bd_users", {});
    if (!users[email]) throw new Error("No account exists with that email.");
    await demoSetPass(users[email], newPass);
    ls.set("bd_users", users);
  },
  async setNewPassword(newPass){
    if (DEMO){
      const s = ls.get("bd_session", null); if (!s) throw new Error("Sign in first.");
      const users = ls.get("bd_users", {});
      await demoSetPass(users[s.email], newPass);
      ls.set("bd_users", users);
      return;
    }
    const aal = await this.getAalState();
    if (aal.needsMfa) {
      const err = new Error("AAL2 session is required to update email or password when MFA is enabled.");
      err.needsMfa = true;
      throw err;
    }
    const { error } = await sb.auth.updateUser({ password: newPass });
    if (error) {
      if (/aal2 session is required/i.test(String(error.message || ""))) {
        error.needsMfa = true;
      }
      throw error;
    }
  },
  async updateName(name){
    if (DEMO){
      const s = ls.get("bd_session", null); if (!s) return;
      const users = ls.get("bd_users", {}); users[s.email].name = name; ls.set("bd_users", users);
      return;
    }
    const { error } = await sb.auth.updateUser({ data: { full_name: name } });
    if (error) throw error;
    await sb.from("profiles").update({ full_name: name }).eq("id", (await sb.auth.getSession()).data.session.user.id);
  },
  async acceptLegalAgreement(version){
    const acceptedAt = new Date().toISOString();
    const legal = {
      version: String(version || ""),
      accepted_at: acceptedAt,
      terms_url: "https://brownsdigital.org/terms/",
      privacy_url: "https://brownsdigital.org/privacy/",
    };
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s) throw new Error("Sign in to continue.");
      const users = ls.get("bd_users", {});
      users[s.email] = { ...(users[s.email] || {}), legal };
      ls.set("bd_users", users);
      return legal;
    }
    const { error } = await sb.auth.updateUser({ data: { legal_agreement: legal } });
    if (error) throw error;
    try {
      const prefs = (await this.fetchAccountPrefs()) || {};
      await this.syncAccountPrefs({ ...prefs, legal_agreement: legal });
    } catch {}
    return legal;
  },
  async updateUsername(username){
    const v = String(username || "").toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 32);
    if (v.length < 3) throw new Error("Username must be at least 3 characters.");
    if (DEMO){
      const s = ls.get("bd_session", null); if (!s) return v;
      const users = ls.get("bd_users", {});
      users[s.email] = { ...(users[s.email] || {}), username: v };
      ls.set("bd_users", users);
      return v;
    }
    const { data: s0 } = await sb.auth.getSession();
    const uid = s0?.session?.user?.id;
    if (!uid) throw new Error("Sign in to change your username.");
    const { error } = await sb.auth.updateUser({ data: { username: v } });
    if (error) throw error;
    try {
      const { error: pe } = await sb.from("profiles").update({ username: v }).eq("id", uid);
      if (pe && !/username|column|schema cache/i.test(String(pe.message || pe))) throw pe;
    } catch (e) {
      if (!/username|column|schema cache/i.test(String(e?.message || e))) throw e;
    }
    try {
      const prefs = (await this.fetchAccountPrefs()) || {};
      prefs.username = v;
      await this.syncAccountPrefs(prefs);
    } catch {}
    _bdInvalidateProfileBundle();
    return v;
  },

  /* ---------------------------- projects ---------------------------- */
  _demoKey(){ const s = ls.get("bd_session", null); return "bd_projects_" + (s?.email || ""); },
  async listProjects(){
    if (DEMO) return ls.get(this._demoKey(), []);
    const { data, error } = await sb.from("projects").select("id,name,updated_at,user_id").order("updated_at", { ascending: false });
    if (error) throw error;
    return data;
  },
  async ownerLabels(ids){
    if (DEMO || !ids?.length) return {};
    const { data, error } = await sb.rpc("bd_owner_labels", { ids });
    if (error || !Array.isArray(data)) return {};
    const map = {};
    data.forEach(row => {
      if (row?.id && row.label) map[row.id] = row.label;
    });
    return map;
  },
  async loadProject(id){
    if (DEMO) return ls.get(this._demoKey(), []).find(x => x.id === id) || null;
    const { data, error } = await sb.from("projects").select("id,name,data,updated_at,user_id").eq("id", id).maybeSingle();
    if (error) throw error;
    return data;
  },
  async saveProject(p, opts){
    const stamp = !opts || opts.stamp !== false;
    if (!DEMO && (!p?.data || typeof p.data !== "object" || !Array.isArray(p.data.pages))) {
      throw new Error("This site has not finished loading. Open it again, then save.");
    }
    if (stamp) p.updated_at = new Date().toISOString();
    if (DEMO){
      const key = this._demoKey(), all = ls.get(key, []);
      const i = all.findIndex(x => x.id === p.id);
      if (i >= 0) all[i] = p; else all.unshift(p);
      ls.set(key, all);
      return p;
    }
    const publicData = JSON.parse(JSON.stringify(p.data));
    if (publicData?.advanced) {
      delete publicData.advanced.discordWebhook;
      delete publicData.advanced.discord_events;
      delete publicData.advanced.discord_messages;
    }
    const row = stamp
      ? { name: p.name, data: publicData, updated_at: p.updated_at }
      : { data: publicData };
    const { data: existing, error: readErr } = await sb.from("projects").select("id").eq("id", p.id).maybeSingle();
    if (readErr) throw readErr;
    if (existing) {
      const { error } = await sb.from("projects").update(row).eq("id", p.id);
      if (error) throw error;
      return p;
    }
    const { data: s } = await sb.auth.getSession();
    const uid = s?.session?.user?.id;
    if (!uid) throw new Error("Sign in to save this site.");
    if (!p.updated_at) p.updated_at = new Date().toISOString();
    const { error } = await sb.from("projects").insert({
      id: p.id,
      user_id: uid,
      name: p.name,
      data: p.data,
      updated_at: p.updated_at,
    });
    if (error) throw error;
    return p;
  },
  async deleteProject(id){
    if (DEMO){
      ls.set(this._demoKey(), ls.get(this._demoKey(), []).filter(x => x.id !== id));
      ["bd_staging","bd_production"].forEach(k => ls.set(k, ls.get(k, []).filter(x => x.project_id !== id)));
      return;
    }
    const domain = await this.getDomain(id);
    if (domain) await this.removeDomain(id);
    return this._deploy({ action:"delete-project", project_id:id });
  },

  /* --------------------------- hosted publishing ---------------------------
     deployStaging(project, slug) → staging URL
     promote(project)             → production URL (/s/{slug}/)
     unpublish(projectId)         → takes production offline
     Server re-verifies the JWT, the plan, slug ownership, file paths,
     size caps, and rate limits — the client is never trusted.        */
  _owner(){ return ls.get("bd_session", null)?.email; },
  async publishRuntime(){
    if (DEMO){
      const sites = {};
      const o = this._owner();
      ls.get("bd_production", []).filter(x => x.owner === o).forEach(x => { sites[x.project_id] = x.runtime || ""; });
      return { runtime: BD_PUBLISH_RUNTIME, sites };
    }
    return this._deploy({ action:"runtime-status" });
  },
  async listDeploys(){
    if (DEMO){
      const o = this._owner();
      return {
        staging:    ls.get("bd_staging", []).filter(x => x.owner === o),
        production: ls.get("bd_production", []).filter(x => x.owner === o)
      };
    }
    const { data, error } = await sb.from("published_sites").select("project_id,slug,env,url,published_at");
    if (error) throw error;
    return { staging: data.filter(x => x.env === "staging"), production: data.filter(x => x.env === "production") };
  },
  async deployStaging(project, slug){
    slug = String(slug).toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
    if (!slug) throw new Error("Choose a site address first.");
    const files = await this._deployFiles(project, STAGING_BASE_URL + slug + "/staging/");
    if (DEMO){
      const o = this._owner();
      const all = ls.get("bd_staging", []).filter(x => !(x.project_id === project.id && x.owner === o));
      const foreign = ls.get("bd_staging", []).concat(ls.get("bd_production", [])).find(x => x.slug === slug && x.owner !== o);
      if (foreign) throw new Error("That web address is already taken by another account. Choose a different one.");
      const own = ls.get("bd_staging", []).concat(ls.get("bd_production", [])).find(x => x.slug === slug && x.owner === o && x.project_id !== project.id);
      if (own) {
        const other = ls.get(this._demoKey(), []).find(p => p.id === own.project_id);
        const label = other?.name || "another site in your account";
        throw new Error(`That address is already used by “${label}”. Multiple sites can't share the same URL — rename one of them or pick a different address.`);
      }
      const rec = { project_id: project.id, owner: o, slug, env:"staging", url: STAGING_BASE_URL + slug + "/staging/", published_at: new Date().toISOString(), runtime: BD_PUBLISH_RUNTIME, files };
      all.unshift(rec); ls.set("bd_staging", all);
      return rec;
    }
    return this._deploy({ action:"staging", slug, project_id: project.id, name: project.name, runtime: BD_PUBLISH_RUNTIME, files, ...this._discordPayload(project) });
  },
  _demoHist(){ return ls.get("bd_publish_history", {}); },
  _demoSaveHist(hist){
    try { ls.set("bd_publish_history", hist); return; } catch {}
    Object.keys(hist).forEach(id => {
      hist[id] = (hist[id] || []).slice(0, 8).map((row, i) => i < 2 ? row : { ...row, files: null, canvas: null });
    });
    try { ls.set("bd_publish_history", hist); } catch {}
  },
  _demoRememberPublish(project, rec, files){
    const hist = this._demoHist();
    const list = Array.isArray(hist[project.id]) ? hist[project.id] : [];
    list.forEach(row => { if (row.status === "published") row.status = "superseded"; });
    const version = list.reduce((n, row) => Math.max(n, Number(row.version) || 0), 0) + 1;
    const id = "dep_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    let canvas = null;
    try { canvas = { name: project.name || "", data: JSON.parse(JSON.stringify(project.data || {})) }; } catch {}
    list.unshift({
      id, version, status: "published",
      published_at: rec.published_at, created_at: rec.published_at,
      runtime: rec.runtime || "", files, canvas
    });
    hist[project.id] = list.slice(0, 8);
    this._demoSaveHist(hist);
    return id;
  },
  async promote(project){
    if (DEMO){
      const o = this._owner();
      const st = ls.get("bd_staging", []).find(x => x.project_id === project.id && x.owner === o);
      if (!st) throw new Error("Publish to staging first, then promote.");
      const prodFiles = await this._deployFiles(project, prodSiteUrl(st.slug));
      const all = ls.get("bd_production", []).filter(x => !(x.project_id === project.id && x.owner === o));
      const rec = { project_id: project.id, owner: o, slug: st.slug, env:"production", url: prodSiteUrl(st.slug), published_at: new Date().toISOString(), runtime: st.runtime || BD_PUBLISH_RUNTIME, files: prodFiles };
      rec.deployment_id = this._demoRememberPublish(project, rec, prodFiles);
      all.unshift(rec); ls.set("bd_production", all);
      return rec;
    }
    const slug = api.deployFor((await this.listDeploys()).staging, project.id)?.slug;
    if (!slug) throw new Error("Publish to staging first, then promote.");
    return this._deploy({
      action: "promote", project_id: project.id,
      ...this._discordPayload(project)
    });
  },
  async unpublish(projectId, project){
    if (DEMO){
      const o = this._owner();
      ls.set("bd_production", ls.get("bd_production", []).filter(x => !(x.project_id === projectId && x.owner === o)));
      return;
    }
    return this._deploy({ action:"unpublish", project_id: projectId, ...this._discordPayload(project) });
  },
  async publishStatus(projectId, deploymentId){
    if (DEMO){
      const o = this._owner();
      const prod = ls.get("bd_production", []).find(x => x.project_id === projectId && x.owner === o) || null;
      const stg = ls.get("bd_staging", []).find(x => x.project_id === projectId && x.owner === o) || null;
      return { staging: stg, production: prod, current_deployment: null, live_release_id: null };
    }
    const body = { action: "publish-status", project_id: projectId };
    if (deploymentId) body.deployment_id = deploymentId;
    return this._deploy(body);
  },
  async listDeployments(projectId){
    if (DEMO){
      const o = this._owner();
      const prod = ls.get("bd_production", []).find(x => x.project_id === projectId && x.owner === o) || null;
      const list = this._demoHist()[projectId] || [];
      return {
        legacy: false,
        deployments: list.map(row => ({
          id: row.id,
          version: row.version,
          status: row.status,
          published_at: row.published_at,
          created_at: row.created_at,
          runtime: row.runtime || "",
          restorable: !!row.files,
          canvas: !!row.canvas,
          live: !!prod && row.id === prod.deployment_id
        }))
      };
    }
    return this._deploy({ action: "list-deployments", project_id: projectId });
  },
  async rollbackDeployment(projectId, deploymentId){
    if (DEMO){
      const o = this._owner();
      const hist = this._demoHist();
      const list = hist[projectId] || [];
      const row = list.find(x => x.id === deploymentId);
      if (!row?.files) throw new Error("Release files for that deployment are no longer on the server.");
      const prod = ls.get("bd_production", []).find(x => x.project_id === projectId && x.owner === o);
      if (!prod) throw new Error("This site is not live.");
      if (prod.deployment_id === deploymentId) throw new Error("That publish is already live.");
      list.forEach(x => { if (x.status === "published") x.status = "superseded"; });
      row.status = "published";
      row.published_at = new Date().toISOString();
      hist[projectId] = list;
      this._demoSaveHist(hist);
      const all = ls.get("bd_production", []).filter(x => !(x.project_id === projectId && x.owner === o));
      all.unshift({ ...prod, files: row.files, runtime: row.runtime || "", deployment_id: row.id, published_at: row.published_at });
      ls.set("bd_production", all);
      return { ok: true, deployment_id: row.id, runtime: row.runtime || "", canvas: !!row.canvas };
    }
    return this._deploy({ action: "rollback", project_id: projectId, deployment_id: deploymentId });
  },
  async deploymentCanvas(projectId, deploymentId){
    if (DEMO){
      const row = (this._demoHist()[projectId] || []).find(x => x.id === deploymentId);
      if (!row?.canvas?.data) throw new Error("That publish has no canvas snapshot.");
      return row.canvas;
    }
    return this._deploy({ action: "deployment-canvas", project_id: projectId, deployment_id: deploymentId });
  },
  _discordPayload(){ return {}; },
  async getDiscordIntegration(projectId){
    if (DEMO) {
      const row = ls.get("bd_discord_integrations", {})[projectId] || {};
      return { connected:!!row.webhook, masked:row.webhook ? "https://discord.com/api/webhooks/.../" + String(row.webhook).slice(-8) : "", events:row.events || {}, messages:row.messages || {} };
    }
    return this._deploy({ action:"discord-status", project_id:projectId });
  },
  async saveDiscordIntegration(projectId, prefs){
    if (DEMO) {
      const all = ls.get("bd_discord_integrations", {});
      all[projectId] = { webhook:prefs.webhook || all[projectId]?.webhook || "", events:prefs.events || {}, messages:prefs.messages || {} };
      ls.set("bd_discord_integrations", all);
      return { connected:!!all[projectId].webhook, masked:all[projectId].webhook ? "https://discord.com/api/webhooks/.../" + String(all[projectId].webhook).slice(-8) : "", events:all[projectId].events, messages:all[projectId].messages };
    }
    return this._deploy({ action:"discord-save", project_id:projectId, discord_webhook:prefs.webhook, discord_events:prefs.events, discord_messages:prefs.messages });
  },
  async clearDiscordIntegration(projectId){
    if (DEMO) {
      const all = ls.get("bd_discord_integrations", {});
      delete all[projectId];
      ls.set("bd_discord_integrations", all);
      return { connected:false, masked:"" };
    }
    return this._deploy({ action:"discord-clear", project_id:projectId });
  },
  async testDiscord(project, previewEvent = "test"){
    if (DEMO){
      const hook = ls.get("bd_discord_integrations", {})[project.id]?.webhook || "";
      if (!/^https:\/\/(discord|discordapp)\.com\/api\/webhooks\//i.test(String(hook).trim())) {
        throw new Error("Add a valid Discord webhook URL first.");
      }
      return { ok: true, demo: true };
    }
    return this._deploy({
      action: "test-discord",
      project_id: project.id,
      discord_preview_event: previewEvent || "test"
    });
  },
  async _deploy(body){
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "publish.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify(body)
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = out.error || deployErrorFallback(res.status);
      throw new Error(msg);
    }
    return out;
  },
  async refreshDomainRouting(projectId){
    if (DEMO) return { routed: false };
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "domain.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "refresh-routing", project_id: projectId })
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || "Custom-domain routing could not be refreshed.");
    return out;
  },
  deployFor: (list, projectId) => (list || []).find(x => x.project_id === projectId) || null,
  _domainAllowsVisits(d){
    if (!d || !d.domain) return false;
    const vs = d.verification_status || (d.status === "verified" ? "verified" : "");
    const ssl = d.ssl_status || "";
    const attach = d.server_attach_status || "";
    if (["active", "verified", "provisioning_ssl"].includes(vs)) return true;
    if (d.status === "verified") return true;
    if (ssl === "active" || attach === "attached" || attach === "cpanel" || attach === "cloudflare") return true; /* brand-ok */
    return false;
  },
  async _deployFiles(project, baseUrl){
    const skip = new Set(["index.php", ".htaccess", "_bd/.htaccess"]);
    let slug = "";
    const m = String(baseUrl || "").match(/\/s\/([a-z0-9-]+)(?:\/|$)/i);
    if (m) slug = m[1].toLowerCase();
    let hasCustomDomain = false;
    let publicBase = baseUrl;
    try {
      const d = await this.getDomain(project.id);
      hasCustomDomain = this._domainAllowsVisits(d);
      /* Production publishes: prefer www.custom-domain in sitemap/llms, never Studio /s/ as the brand URL. */
      const isStaging = /\/staging\/?/i.test(String(baseUrl || ""));
      if (!isStaging && hasCustomDomain && d?.domain) {
        const apex = String(d.domain).toLowerCase().replace(/^www\./, "");
        if (apex) publicBase = "https://www." + apex + "/";
      }
    } catch { /* publish without domain context */ }
    return buildSiteFiles(project, publicBase, { slug, hasCustomDomain }).filter(f => !skip.has(f.path));
  },

  /* ----------------------------- domains ----------------------------- */
  _legacyDomainStatus(vs, fallback){
    if (fallback === "verified" || fallback === "pending") return fallback;
    return ["verified", "provisioning_ssl", "active"].includes(vs) ? "verified" : "pending";
  },
  async listSiteDomains(projectId){
    if (DEMO){
      const o = this._owner();
      const rows = ls.get("bd_domains", []).filter(d => d.project_id === projectId && d.owner === o);
      const prod = ls.get("bd_production", []).find(x => x.project_id === projectId && x.owner === o);
      return {
        production: prod
          ? { hostname: prodSiteHostLabel(prod.slug), url: prod.url, active: true, slug: prod.slug }
          : { hostname: null, url: null, active: false, slug: null },
        domains: rows.map((d, i) => ({
          id: d.id || null,
          domain: d.domain,
          token: d.token,
          status: d.status,
          verification_status: d.status === "verified" ? "active" : "pending",
          ssl_status: d.status === "verified" ? "active" : "none",
          is_primary: i === 0,
          dns: d.dns || null
        }))
      };
    }
    return this._domain({ action: "list", project_id: projectId });
  },
  async getDomain(projectId){
    if (DEMO){
      return ls.get("bd_domains", []).find(d => d.project_id === projectId && d.owner === this._owner()) || null;
    }
    try {
      const list = await this.listSiteDomains(projectId);
      const domains = list?.domains || [];
      if (domains.length) {
        const primary = domains.find(d => d.is_primary) || domains[0];
        return {
          ...primary,
          status: this._legacyDomainStatus(primary.verification_status, primary.status),
          production: list.production || null
        };
      }
    } catch { /* fall through to legacy custom_domains */ }
    try {
      const { data } = await sb.from("custom_domains").select("domain,token,status,created_at").eq("project_id", projectId).maybeSingle();
      if (data) return data;
    } catch { /* ignore */ }
    const pending = await this._domain({ action:"status", project_id: projectId });
    return pending?.domain ? pending : null;
  },
  async requestDomain(projectId, domain){
    domain = String(domain).toLowerCase().trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!/^([a-z0-9-]+\.)+[a-z]{2,}$/.test(domain)) throw new Error("Enter a valid domain, like www.example.co.za");
    if (DEMO){
      const rec = { project_id: projectId, owner: this._owner(), domain, token: "bd-verify-" + uid() + uid(), status: "pending", created_at: new Date().toISOString() };
      const all = ls.get("bd_domains", []).filter(d => !(d.project_id === projectId && d.owner === rec.owner));
      all.push(rec); ls.set("bd_domains", all);
      return rec;
    }
    return this._domain({ action:"request", project_id: projectId, domain });
  },
  async verifyDomain(projectId){
    if (DEMO){
      const all = ls.get("bd_domains", []);
      const rec = all.find(d => d.project_id === projectId && d.owner === this._owner());
      if (!rec) throw new Error("Request a domain first.");
      rec.status = "verified"; ls.set("bd_domains", all);
      return Object.assign({}, rec, { demo: true, verification_status: "active", ssl_status: "active" });
    }
    return this._domain({ action:"verify", project_id: projectId });
  },
  async removeDomain(projectId, domainId){
    if (DEMO){
      ls.set("bd_domains", ls.get("bd_domains", []).filter(d => !(d.project_id === projectId && d.owner === this._owner())));
      return;
    }
    const body = { action:"remove", project_id: projectId };
    if (domainId) body.domain_id = domainId;
    return this._domain(body);
  },
  async setPrimaryDomain(projectId, domainId){
    if (DEMO) return { ok: true, id: domainId };
    return this._domain({ action: "set-primary", project_id: projectId, domain_id: domainId });
  },
  async checkSiteDomain(projectId, domainId){
    if (DEMO) return this.getDomain(projectId);
    return this._domain({ action: "check", project_id: projectId, domain_id: domainId });
  },
  async _domain(body){
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "domain.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify(body)
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || "Domain request failed.");
    return out;
  },

  /* ----------------------------- data export ----------------------------- */
  async requestDataExport(scope){
    if (DEMO) return this._demoDataExport(scope);
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "export.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify({ scope })
    });
    if (!res.ok) {
      const out = await res.json().catch(() => ({}));
      throw new Error(out.error || "Export failed.");
    }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^";\n]+)/i);
    const filename = match ? match[1] : "bd-studio-data-export-" + Date.now() + ".json";
    return { blob, filename };
  },

  _demoDataExport(scope){
    const s = ls.get("bd_session", null);
    if (!s) throw new Error("Sign in first.");
    const users = ls.get("bd_users", {}) || {};
    const u = users[s.email] || {};
    const exportedMs = Date.now();
    const payload = {
      exportedAt: new Date().toISOString(),
      profile: {
        email: s.email,
        emailVerified: true,
        displayName: u.name || s.name || "",
        handle: (s.email || "").split("@")[0] || "user",
        plan: u.plan || "free",
        exportedAt: exportedMs,
        themePrefs: { active: null, saved: [] },
        builderPrefs: u.builder_prefs || {}
      },
      sites: scope === "account" || scope === "storage" ? [] : ls.get("bd_projects_" + s.email, []).map(p => ({
        id: p.id,
        name: p.name,
        updatedAt: p.updated_at ? Date.parse(p.updated_at) : null,
        pageCount: (p.data?.pages || []).length,
        pages: (p.data?.pages || []).map(pg => ({
          id: pg.id || "",
          name: pg.name || "",
          slug: pg.slug || "",
          blockCount: (pg.blocks || []).length
        }))
      })),
      storage: { flows: [], variables: [] },
      oauthGrants: [],
      passkeyCount: 0,
      note: "Demo export — metadata only.",
      ...(scope !== "all" ? { exportScope: scope } : {})
    };
    const json = JSON.stringify(payload, null, 2);
    return {
      blob: new Blob([json], { type: "application/json" }),
      filename: "bd-studio-data-export-" + exportedMs + ".json"
    };
  },

  downloadExportFile({ blob, filename }){
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /* ---------------------------- activity ---------------------------- */
  async logActivity(projectId, kind, message){
    const rec = { id: uid(), project_id: projectId, kind, message, created_at: new Date().toISOString() };
    if (DEMO){
      const key = "bd_activity_" + (this._owner() || "");
      const all = ls.get(key, []); all.unshift(rec); if (all.length > 100) all.length = 100;
      ls.set(key, all);
      return rec;
    }
    const { data: s } = await sb.auth.getSession();
    if (!s.session) return rec;
    await sb.from("site_activity").insert({ user_id: s.session.user.id, project_id: projectId, kind, message });
    return rec;
  },
  async listActivity(projectId, limit = 20, offset = 0){
    if (DEMO){
      let all = ls.get("bd_activity_" + (this._owner() || ""), []);
      if (projectId) all = all.filter(a => a.project_id === projectId);
      return all.slice(offset, offset + limit);
    }
    let q = sb.from("site_activity").select("project_id,kind,message,created_at").order("created_at", { ascending: false }).range(offset, offset + limit - 1);
    if (projectId) q = q.eq("project_id", projectId);
    const { data, error } = await q;
    return error ? [] : data;
  },

  /* -------------------------- collaborators --------------------------
     Pro: up to 3 per site · Studio: unlimited · Free: none.
     Live mode stores rows in site_collaborators (RLS: owner writes,
     owner + member read). Invites are "pending" until accepted.     */
  async listCollaborators(projectId){
    if (DEMO) return ls.get("bd_collab", []).filter(c => c.project_id === projectId && c.owner === this._owner());
    const { data, error } = await sb.from("site_collaborators")
      .select("id,project_id,email,role,status,invite_token,invite_expires_at,created_at")
      .eq("project_id", projectId).order("created_at");
    return error ? [] : data;
  },
  async inviteCollaborator(projectId, email, role){
    email = String(email || "").trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error("Enter a valid email address.");
    if (DEMO){
      const all = ls.get("bd_collab", []);
      if (all.some(c => c.project_id === projectId && c.email === email && c.status !== "pending")) {
        throw new Error("That person is already invited.");
      }
      const token = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, "0")).join("");
      const existing = all.find(c => c.project_id === projectId && c.email === email);
      const rec = existing ? Object.assign(existing, { role, status: "pending", invite_kind: "email", invite_token: token }) : {
        id: uid(), project_id: projectId, owner: this._owner(), email, role,
        status: "pending", invite_kind: "email", invite_token: token,
        created_at: new Date().toISOString()
      };
      if (!existing) all.push(rec);
      ls.set("bd_collab", all);
      return rec;
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "cowork.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "invite", project_id: projectId, email, role })
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || "Could not send invite.");
    return out.collaborator || out;
  },
  isCoworkLinkInvite(row){
    if (!row) return false;
    if (row.invite_kind === "link") return true;
    return /^link\+[a-f0-9]+@cowork\.invite$/i.test(String(row.email || ""));
  },
  coworkInviteToken(row){
    if (row?.invite_token) return String(row.invite_token);
    const m = String(row?.email || "").match(/^link\+([a-f0-9]+)@cowork\.invite$/i);
    return m ? m[1] : "";
  },
  coworkInviteUrl(projectId, token){
    const origin = (typeof location !== "undefined" ? location.origin : "https://studio.brownsdigital.org");
    const u = new URL("/crew", origin);
    u.searchParams.set("cowork", token);
    u.searchParams.set("site", projectId);
    return u.toString();
  },
  async createCollaboratorLink(projectId, role){
    const token = Array.from(crypto.getRandomValues(new Uint8Array(12)))
      .map(b => b.toString(16).padStart(2, "0")).join("");
    const email = `link+${token}@cowork.invite`;
    if (DEMO){
      const all = ls.get("bd_collab", []);
      const rec = {
        id: uid(), project_id: projectId, owner: this._owner(), email, role,
        status: "pending", invite_kind: "link", invite_token: token,
        created_at: new Date().toISOString()
      };
      all.push(rec); ls.set("bd_collab", all);
      return { ...rec, link: this.coworkInviteUrl(projectId, token) };
    }
    const { data: s } = await sb.auth.getSession();
    const expires = new Date(Date.now() + 7 * 86400000).toISOString();
    const { data, error } = await sb.from("site_collaborators")
      .insert({
        project_id: projectId, email, role, status: "pending",
        invited_by: s.session.user.id, invite_token: token, invite_expires_at: expires
      }).select().single();
    if (error) throw new Error(error.message);
    return { ...data, invite_kind: "link", invite_token: token, link: this.coworkInviteUrl(projectId, token) };
  },
  async updateCollaborator(id, role){
    if (DEMO){
      const all = ls.get("bd_collab", []);
      const c = all.find(x => x.id === id); if (c) c.role = role;
      ls.set("bd_collab", all); return;
    }
    await sb.from("site_collaborators").update({ role }).eq("id", id);
  },
  async removeCollaborator(id){
    if (DEMO){ ls.set("bd_collab", ls.get("bd_collab", []).filter(x => x.id !== id)); return; }
    await sb.from("site_collaborators").delete().eq("id", id);
  },
  async acceptCoworkInvite(projectId, token){
    token = String(token || "").trim().toLowerCase();
    if (!token || !projectId) throw new Error("Invalid invite link.");
    if (DEMO){
      const all = ls.get("bd_collab", []);
      const row = all.find(c => c.project_id === projectId && (
        c.invite_token === token || /^link\+/.test(c.email || "") && c.email.includes(token)
      ));
      if (!row) throw new Error("This invite link is invalid or has already been used.");
      if (row.status === "active") return { site_name: "this site", already: true };
      const me = ls.get("bd_session", null);
      if (!me?.email) throw new Error("Sign in to accept this invite.");
      if (!/^link\+/.test(row.email || "") && row.email !== me.email.toLowerCase()) {
        throw new Error("Sign in with " + row.email + " to accept this invite.");
      }
      row.status = "active";
      ls.set("bd_collab", all);
      return { site_name: "this site" };
    }
    const authToken = await this.accessToken();
    const res = await bdFetch(API_BASE + "cowork.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + authToken },
      body: JSON.stringify({ action: "accept", project_id: projectId, token })
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || "Could not accept invite.");
    return out;
  },

  /* ------------------------- builder prefs sync -------------------------
     Preferences live in localStorage for instant reads; live mode also
     mirrors them to profiles.builder_prefs so they roam across devices. */
  async syncBuilderPrefs(prefs){
    if (DEMO) return;
    try {
      const { data: s } = await sb.auth.getSession();
      if (s.session) await sb.from("profiles").update({ builder_prefs: prefs }).eq("id", s.session.user.id);
      _bdInvalidateProfileBundle();
    } catch {}
  },
  async fetchProfileBundle(){
    if (DEMO) return {};
    return _bdFetchProfileBundleLive();
  },
  warmProfile(){
    if (DEMO) return Promise.resolve({});
    return _bdFetchProfileBundleLive();
  },
  async fetchBuilderPrefs(){
    if (DEMO) return null;
    try {
      const data = await _bdFetchProfileBundleLive();
      return data?.builder_prefs || null;
    } catch { return null; }
  },

  async syncThemePrefs(prefs){
    if (DEMO){
      try { localStorage.setItem("bd_theme_account", JSON.stringify(prefs)); } catch {}
      return;
    }
    try {
      const { data: s } = await sb.auth.getSession();
      if (s.session) await sb.from("profiles").update({ theme_prefs: prefs }).eq("id", s.session.user.id);
      _bdInvalidateProfileBundle();
    } catch {}
  },
  async syncAvatar(dataUrl){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (s?.email) try { localStorage.setItem("bd_avatar_" + s.email, dataUrl || ""); } catch {}
      return;
    }
    try {
      const { data: s } = await sb.auth.getSession();
      if (!s.session) return;
      await sb.from("profiles").update({ avatar_url: dataUrl || null }).eq("id", s.session.user.id);
      _bdInvalidateProfileBundle();
    } catch {}
  },
  async fetchAvatar(){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s?.email) return null;
      return localStorage.getItem("bd_avatar_" + s.email) || null;
    }
    try {
      const data = await _bdFetchProfileBundleLive();
      return data?.avatar_url || null;
    } catch { return null; }
  },
  async fetchProfileIdentity(){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s?.email) return { name: "", avatar: null, oauthProvider: "" };
      const u = ls.get("bd_users", {})[s.email] || {};
      let avatar = null;
      try { avatar = localStorage.getItem("bd_avatar_" + s.email) || null; } catch {}
      return { name: u.name || "", avatar, oauthProvider: "" };
    }
    try {
      const { data: s } = await sb.auth.getSession();
      const user = s?.session?.user || null;
      const data = await _bdFetchProfileBundleLive();
      const profileAvatar = data?.avatar_url || null;
      const oauthAvatar = _bdAvatarFromAuthUser(user);
      return {
        name: String(data?.full_name || "").trim() || String(user?.user_metadata?.full_name || user?.user_metadata?.name || "").trim(),
        avatar: profileAvatar || oauthAvatar || null,
        oauthAvatar: oauthAvatar || null,
        oauthProvider: _bdOAuthProviderFromAuthUser(user),
        fromOAuth: !profileAvatar && !!oauthAvatar
      };
    } catch {
      return { name: "", avatar: null, oauthProvider: "" };
    }
  },
  async fetchThemePrefs(){
    if (DEMO){
      try { return JSON.parse(localStorage.getItem("bd_theme_account") || '{"active":null,"saved":[]}'); }
      catch { return { active:null, saved:[] }; }
    }
    try {
      const data = await _bdFetchProfileBundleLive();
      return data?.theme_prefs || { active:null, saved:[] };
    } catch { return { active:null, saved:[] }; }
  },

  /* ------------------------- account prefs sync -------------------------
     Privacy, UI, notifications, shortcuts, pins, and layout prefs roam
     across devices via profiles.account_prefs in live mode. */
  async syncAccountPrefs(prefs){
    if (DEMO) return;
    try {
      const { data: s } = await sb.auth.getSession();
      if (!s.session) return;
      const { error } = await sb.from("profiles").update({ account_prefs: prefs }).eq("id", s.session.user.id);
      if (error) {
        console.error("[bd] account prefs save failed", error);
        return;
      }
      _bdInvalidateProfileBundle();
    } catch (err) {
      console.error("[bd] account prefs save failed", err);
    }
  },
  async fetchAccountPrefs(){
    if (DEMO) return null;
    try {
      const data = await _bdFetchProfileBundleLive();
      return data?.account_prefs || null;
    } catch { return null; }
  },

  /* ------------------------------- MFA ------------------------------- */
  async _mfaList(){
    if (!sb?.auth?.mfa?.listFactors) return { all: [], totp: [], webauthn: [] };
    const { data, error } = await sb.auth.mfa.listFactors();
    if (error) throw error;
    const all = Array.isArray(data?.all) ? data.all : [];
    const byType = t => all.filter(f =>
      String(f.factor_type || f.type || "").toLowerCase() === t &&
      String(f.status || "").toLowerCase() !== "unverified");
    return { all, totp: byType("totp"), webauthn: byType("webauthn") };
  },
  async _mfaListAll(){
    if (!sb?.auth?.mfa?.listFactors) return [];
    const { data, error } = await sb.auth.mfa.listFactors();
    if (error) throw error;
    return Array.isArray(data?.all) ? data.all : [];
  },
  async _mfaCleanupTotpStale(){
    if (!sb?.auth?.mfa?.unenroll) return;
    const all = await this._mfaListAll();
    const stale = all.filter(f =>
      String(f.factor_type || f.type || "").toLowerCase() === "totp" &&
      String(f.status || "").toLowerCase() !== "verified");
    for (const f of stale) {
      const id = f?.id || f?.factor_id;
      if (!id) continue;
      await sb.auth.mfa.unenroll({ factorId: id }).catch(() => {});
    }
  },
  async _mfaSyncFlags(flags = {}){
    if (DEMO || !sb) return;
    const { data: s } = await sb.auth.getSession();
    if (!s?.session) return;
    const patch = {};
    if (typeof flags.mfa_totp_enabled === "boolean") patch.mfa_totp_enabled = flags.mfa_totp_enabled;
    if (typeof flags.mfa_passkey_enabled === "boolean") patch.mfa_passkey_enabled = flags.mfa_passkey_enabled;
    if (typeof flags.mfa_enabled_at !== "undefined") patch.mfa_enabled_at = flags.mfa_enabled_at;
    if (!Object.keys(patch).length) return;
    try { await sb.from("profiles").update(patch).eq("id", s.session.user.id); } catch {}
  },
  async getAalState(){
    if (DEMO || !sb) return { current: "aal1", next: "aal1", needsMfa: false };
    const fn = sb.auth.mfa?.getAuthenticatorAssuranceLevel;
    if (!fn) return { current: "aal1", next: "aal1", needsMfa: false };
    const { data, error } = await fn();
    if (error) throw error;
    const current = data?.currentLevel || "aal1";
    const next = data?.nextLevel || current;
    return { current, next, needsMfa: current === "aal1" && next === "aal2" };
  },
  async mfaElevateTotp(code){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("MFA verification");
    const factors = await this._mfaList();
    const factor = (factors.totp || []).find(f => f.status === "verified") || factors.totp[0];
    if (!factor?.id) throw new Error("No authenticator is set up on this account.");
    const challengeFn = sb.auth.mfa?.challenge;
    const verifyFn = sb.auth.mfa?.verify;
    if (!challengeFn || !verifyFn) throw new Error("Authenticator verification is unavailable right now.");
    const { data: ch, error: chErr } = await challengeFn({ factorId: factor.id });
    if (chErr) throw chErr;
    const { error: vErr } = await verifyFn({ factorId: factor.id, challengeId: ch?.id, code: String(code || "").trim() });
    if (vErr) throw vErr;
    return { ok: true };
  },
  async getMfaState(){
    if (DEMO) return { enabled: false, totpEnabled: false, passkeyEnabled: false, factors: { all: [], totp: [], webauthn: [] } };
    const factors = await this._mfaList();
    const totpEnabled = factors.totp.length > 0;
    const passkeyEnabled = factors.webauthn.length > 0;
    this._mfaSyncFlags({
      mfa_totp_enabled: totpEnabled,
      mfa_passkey_enabled: passkeyEnabled,
      mfa_enabled_at: (totpEnabled || passkeyEnabled) ? new Date().toISOString() : null
    }).catch(() => {});
    return { enabled: totpEnabled || passkeyEnabled, totpEnabled, passkeyEnabled, factors };
  },
  async getPasskeyState(){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("Passkey setup");
    const passkeyApi = sb?.auth?.passkey;
    if (!passkeyApi?.list) throw new Error("Passkey management is unavailable right now.");
    const { data, error } = await passkeyApi.list();
    if (error) throw error;
    const all = Array.isArray(data) ? data : (Array.isArray(data?.passkeys) ? data.passkeys : []);
    const enabled = all.length > 0;
    this._mfaSyncFlags({
      mfa_passkey_enabled: enabled,
      mfa_enabled_at: enabled ? new Date().toISOString() : null
    }).catch(() => {});
    return { enabled, passkeys: all };
  },
  async mfaEnrollTotp(){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("MFA setup");
    if (!sb?.auth?.mfa?.enroll) throw new Error("Authenticator setup is unavailable right now.");
    await this._mfaCleanupTotpStale();
    const brand = "Browns Digital";
    const enrollOnce = (opts) => sb.auth.mfa.enroll(opts);
    let data, error;
    ({ data, error } = await enrollOnce({ factorType: "totp", friendlyName: brand, issuer: brand }));
    if (error && /issuer/i.test(String(error.message || ""))) {
      ({ data, error } = await enrollOnce({ factorType: "totp", friendlyName: brand }));
    }
    if (error && /friendly name/i.test(String(error.message || ""))) {
      await this._mfaCleanupTotpStale();
      ({ data, error } = await enrollOnce({ factorType: "totp", friendlyName: brand, issuer: brand }));
      if (error && /issuer/i.test(String(error.message || ""))) {
        ({ data, error } = await enrollOnce({ factorType: "totp", friendlyName: brand }));
      }
    }
    if (error) throw error;
    const secret = String(data?.totp?.secret || "").replace(/\s+/g, "");
    // Always rebuild otpauth + QR ourselves. Supabase's QR encodes Site URL:email.
    const uri = brandTotpAuthUri(secret, data?.totp?.uri || "", brand);
    const qr = await loadQrCode().then(() => brandTotpQrDataUrl(uri));
    return {
      factorId: data?.id || "",
      qr,
      secret,
      uri
    };
  },
  async mfaVerifyTotp(factorId, code){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("MFA setup");
    const challengeFn = sb?.auth?.mfa?.challenge;
    const verifyFn = sb?.auth?.mfa?.verify;
    if (!challengeFn || !verifyFn) throw new Error("Authenticator verification is unavailable right now.");
    const { data: ch, error: chErr } = await challengeFn({ factorId });
    if (chErr) throw chErr;
    const { error: vErr } = await verifyFn({ factorId, challengeId: ch?.id, code: String(code || "").trim() });
    if (vErr) throw vErr;
    await this._mfaSyncFlags({ mfa_totp_enabled: true, mfa_enabled_at: new Date().toISOString() });
    return { ok: true };
  },
  async mfaEnrollPasskey(){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("Passkey setup");
    if (!window.isSecureContext) throw new Error("Passkeys need HTTPS on this device.");
    if (!navigator?.credentials?.create) throw new Error("This browser can’t create passkeys.");

    const passkeyApi = sb?.auth?.passkey;
    if (!passkeyApi?.startRegistration || !passkeyApi?.verifyRegistration){
      throw new Error("Passkey setup isn’t available in this browser right now.");
    }

    const finishOk = async () => {
      await this._mfaSyncFlags({ mfa_passkey_enabled: true, mfa_enabled_at: new Date().toISOString() });
      return { ok: true };
    };
    const alreadyLinked = async () => {
      try {
        const state = await this.getPasskeyState();
        return !!state.enabled;
      } catch {
        return false;
      }
    };
    const isAlreadyErr = (e) => /webauthn_credential_exists|already/i.test(String(e?.code || e?.error_code || e?.message || ""));

    const { data: start, error: startError } = await passkeyApi.startRegistration();
    if (startError) throw startError;
    const options = start?.options || start?.publicKey || start;
    const challengeId = start?.challenge_id || start?.challengeId;
    if (!options || !challengeId) throw new Error("Couldn't start passkey setup. Please try again.");

    let credential;
    try {
      credential = await navigator.credentials.create({
        publicKey: passkeyCreationOptions(options),
      });
    } catch (e) {
      if (await alreadyLinked()) return await finishOk();
      if (passkeyIsCancelErr(e)){
        const err = new Error("Passkey setup was cancelled.");
        err.name = e?.name || "NotAllowedError";
        err.cause = e;
        throw err;
      }
      throw e;
    }
    if (!credential) throw new Error("Passkey setup was cancelled.");

    const meta = passkeyClientMeta(credential, options);
    const { error: verifyError } = await passkeyApi.verifyRegistration({
      challengeId,
      credential: passkeyRegistrationJson(credential),
    });
    if (verifyError && !isAlreadyErr(verifyError)){
      if (await alreadyLinked()) return await finishOk();
      console.warn("[passkey] verify failed", verifyError, meta);
      const originOk = !meta.clientOrigin || meta.clientOrigin === meta.pageOrigin;
      const knownHost = /^https:\/\/((studio|staff)\.)?brownsdigital\.org$/i.test(meta.pageOrigin || "");
      if (!originOk || !knownHost){
        throw new Error("Couldn't save that passkey. Stay on brownsdigital.org or studio.brownsdigital.org, then try again.");
      }
      throw new Error("Couldn't save that passkey. Please try again.");
    }
    return await finishOk();
  },
  async passkeyRemove(passkeyId){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("Passkey management");
    const passkeyApi = sb?.auth?.passkey;
    if (!passkeyApi?.delete) throw new Error("Passkey removal is unavailable right now.");
    let out;
    try { out = await passkeyApi.delete(passkeyId); }
    catch {
      out = await passkeyApi.delete({ id: passkeyId });
    }
    if (out?.error) throw out.error;
    const state = await this.getPasskeyState();
    await this._mfaSyncFlags({
      mfa_passkey_enabled: state.enabled,
      mfa_enabled_at: state.enabled ? new Date().toISOString() : null
    });
    return { ok: true };
  },
  async passkeyRemoveAll(){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("Passkey management");
    const state = await this.getPasskeyState();
    for (const p of (state.passkeys || [])) {
      const id = p?.id || p?.passkey_id || p?.credential_id;
      if (!id) continue;
      await this.passkeyRemove(id).catch(() => {});
    }
    const next = await this.getPasskeyState();
    await this._mfaSyncFlags({
      mfa_passkey_enabled: next.enabled,
      mfa_enabled_at: next.enabled ? new Date().toISOString() : null
    });
    return { ok: true };
  },
  async mfaDisableTotpAll(){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("MFA management");
    if (!sb?.auth?.mfa?.unenroll) throw new Error("Authenticator removal is unavailable right now.");
    const all = await this._mfaListAll();
    const totp = all.filter(f => String(f.factor_type || f.type || "").toLowerCase() === "totp");
    let removed = 0;
    for (const f of totp) {
      const id = f?.id || f?.factor_id;
      if (!id) continue;
      const { error } = await sb.auth.mfa.unenroll({ factorId: id });
      if (error) throw error;
      removed += 1;
    }
    const state = await this.getMfaState();
    if (state.totpEnabled) throw new Error("The authenticator factor is still enabled. Verify your code and try again.");
    return { ok: true, removed };
  },
  async mfaDisableFactor(factorId, kind = "totp"){
    if (DEMO || !_bdLiveConfig || !sb) throw mfaLiveError("MFA management");
    if (!sb?.auth?.mfa?.unenroll) throw new Error("Authenticator removal is unavailable right now.");
    const { error } = await sb.auth.mfa.unenroll({ factorId });
    if (error) throw error;
    const state = await this.getMfaState();
    await this._mfaSyncFlags({
      mfa_totp_enabled: kind === "totp" ? state.totpEnabled : undefined,
      mfa_passkey_enabled: kind === "webauthn" ? state.passkeyEnabled : undefined,
      mfa_enabled_at: state.enabled ? new Date().toISOString() : null
    });
    return { ok: true };
  },

  /* ----------------------------- suspension ----------------------------- */
  async isSuspended(){
    if (DEMO){
      const s = ls.get("bd_session", null);
      return s ? !!ls.get("bd_users", {})[s.email]?.suspended : false;
    }
    const data = await _bdFetchProfileBundleLive();
    if (data && Object.prototype.hasOwnProperty.call(data, "suspended")) return !!data.suspended;
    return false;
  },
  async hasQaAccess(){
    if (!QA_MODE) return true;
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s) return false;
      const u = ls.get("bd_users", {})[s.email];
      return !!(u?.qa_access || u?.is_admin);
    }
    const data = await _bdFetchProfileBundleLive();
    if (!data) return false;
    if (Object.prototype.hasOwnProperty.call(data, "qa_access") || Object.prototype.hasOwnProperty.call(data, "is_admin")) {
      return !!(data.qa_access || data.is_admin);
    }
    return false;
  },

  /* ------------------------- platform admin -------------------------
     Live mode: every call goes through api/admin.php, which re-verifies
     the JWT AND profiles.is_admin with the service role, performs the
     cross-tenant operation, and writes a row to admin_audit. The
     service key never reaches the browser.
     Demo mode: operates on this browser's localStorage tenants so the
     whole admin surface is explorable offline.                       */
  async _ensureAdminMfa(){
    const aal = await this.getAalState();
    if (aal.current === "aal2") return;
    const mfa = await this.getMfaState();
    if (!mfa.totpEnabled) {
      throw new Error("Turn on an authenticator in Account before using admin tools.");
    }
    if (typeof promptAuthenticatorCode !== "function") {
      throw new Error("Admin tools require an authenticator code. Sign in with it, then try again.");
    }
    const code = await promptAuthenticatorCode({
      title: "Enter authenticator code",
      product: "Studio admin",
      sub: "Admin tools need the code from your authenticator app.",
      verify: (c) => this.mfaElevateTotp(c)
    });
    if (!code) throw new Error("Admin tools need an authenticator code.");
  },
  async admin(action, payload = {}){
    if (!DEMO){
      await this._ensureAdminMfa();
      const token = await this.accessToken();
      const res = await bdFetch(API_BASE + "admin.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify(Object.assign({ action }, payload))
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || "Admin request failed (" + res.status + ").");
      return out;
    }
    /* ---------- demo implementations ---------- */
    const users = ls.get("bd_users", {});
    const audit = (act, target, detail) => {
      const all = ls.get("bd_admin_audit", []);
      all.unshift({ id: uid(), admin: this._owner(), action: act, target, detail: detail || "", created_at: new Date().toISOString() });
      if (all.length > 300) all.length = 300;
      ls.set("bd_admin_audit", all);
    };
    if (action === "metrics"){
      const perPlan = { free:0, pro:0, studio:0 };
      let sites = 0;
      Object.entries(users).forEach(([email, u]) => { perPlan[u.plan || "free"] = (perPlan[u.plan || "free"] || 0) + 1; sites += ls.get("bd_projects_" + email, []).length; });
      return { users: Object.keys(users).length, perPlan, sites,
        published: ls.get("bd_production", []).length, staged: ls.get("bd_staging", []).length,
        domains: ls.get("bd_domains", []).length };
    }
    if (action === "list_users"){
      return { users: Object.entries(users).map(([email, u]) => ({
        email, name: u.name || "", plan: u.plan || "free", suspended: !!u.suspended,
        qa_access: !!u.qa_access,
        created_at: u.created_at || null, last_sign_in: u.last_sign_in || null,
        sites: ls.get("bd_projects_" + email, []).length })) };
    }
    if (action === "create_user"){
      const email = String(payload.email || "").trim().toLowerCase();
      const name = String(payload.name || "").trim();
      const password = String(payload.password || "");
      const plan = payload.plan || "studio";
      if (!email || !name) throw new Error("Name and email are required.");
      if (password.length < 8) throw new Error("Use a password of at least 8 characters.");
      if (users[email]) throw new Error("An account with that email already exists.");
      users[email] = { name, plan, verified: true, qa_access: !!QA_MODE, created_at: new Date().toISOString() };
      await demoSetPass(users[email], password);
      ls.set("bd_users", users);
      audit("create_user", email, "Invited — plan " + plan);
      return { ok: true, email };
    }
    if (action === "set_qa_access"){
      if (!users[payload.email]) throw new Error("No such user.");
      users[payload.email].qa_access = !!payload.qa_access;
      ls.set("bd_users", users);
      audit(payload.qa_access ? "grant_qa" : "revoke_qa", payload.email, "");
      return { ok: true };
    }
    if (action === "set_plan"){
      if (!users[payload.email]) throw new Error("No such user.");
      users[payload.email].plan = payload.plan; ls.set("bd_users", users);
      audit("plan_change", payload.email, "Plan set to " + payload.plan + (payload.reason ? " — " + payload.reason : ""));
      return { ok: true };
    }
    if (action === "suspend"){
      if (!users[payload.email]) throw new Error("No such user.");
      users[payload.email].suspended = !!payload.suspended; ls.set("bd_users", users);
      audit(payload.suspended ? "suspend" : "unsuspend", payload.email, payload.reason || "");
      return { ok: true };
    }
    if (action === "delete_user"){
      delete users[payload.email]; ls.set("bd_users", users);
      ls.del("bd_projects_" + payload.email);
      ["bd_staging","bd_production","bd_domains"].forEach(k => ls.set(k, ls.get(k, []).filter(x => x.owner !== payload.email)));
      audit("delete_user", payload.email, "GDPR delete — account and all sites removed");
      return { ok: true };
    }
    if (action === "list_sites"){
      const rows = [];
      Object.keys(users).forEach(email => {
        ls.get("bd_projects_" + email, []).forEach(p => {
          const stg = ls.get("bd_staging", []).find(x => x.project_id === p.id && x.owner === email);
          const prod = ls.get("bd_production", []).find(x => x.project_id === p.id && x.owner === email);
          rows.push({ id: p.id, name: p.name, owner: email, pages: (p.data?.pages || []).length,
            updated_at: p.updated_at, slug: prod?.slug || stg?.slug || "", staging: stg?.url || "", live: prod?.url || "" });
        });
      });
      return { sites: rows };
    }
    if (action === "get_site"){
      const p = ls.get("bd_projects_" + payload.owner, []).find(x => x.id === payload.project_id);
      if (!p) throw new Error("Site not found.");
      return { site: p };
    }
    if (action === "patch_site"){
      const key = "bd_projects_" + payload.owner, all = ls.get(key, []);
      const i = all.findIndex(x => x.id === payload.project_id);
      if (i < 0) throw new Error("Site not found.");
      all[i].data = payload.data; all[i].updated_at = new Date().toISOString();
      ls.set(key, all);
      audit("patch_site", payload.owner + " / " + all[i].name, "God-mode patch via Admin");
      return { ok: true };
    }
    if (action === "force_unpublish"){
      ls.set("bd_production", ls.get("bd_production", []).filter(x => !(x.project_id === payload.project_id && x.owner === payload.owner)));
      audit("force_unpublish", payload.owner + " / " + payload.project_id, payload.reason || "");
      return { ok: true };
    }
    if (action === "list_domains"){
      return { domains: ls.get("bd_domains", []).map(d => ({ domain: d.domain, owner: d.owner, project_id: d.project_id, status: d.status, token: d.token, created_at: d.created_at })) };
    }
    if (action === "domain_set"){
      const all = ls.get("bd_domains", []);
      const d = all.find(x => x.domain === payload.domain);
      if (!d) throw new Error("Domain not found.");
      if (payload.status === "removed"){ ls.set("bd_domains", all.filter(x => x !== d)); }
      else { d.status = payload.status; ls.set("bd_domains", all); }
      audit("domain_" + payload.status, payload.domain, "");
      return { ok: true };
    }
    if (action === "audit_log"){
      let all = ls.get("bd_admin_audit", []);
      Object.keys(users).forEach(email => {
        ls.get("bd_activity_" + email, []).slice(0, 20).forEach(a =>
          all.push({ id: a.id, admin: email, action: a.kind, target: a.project_id || "", detail: a.message, created_at: a.created_at, tenant: true }));
      });
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return { log: all.slice(0, 200) };
    }
    throw new Error("Unknown admin action.");
  },

  /* ------------------------------ credits & Paystack ------------------------------ */
  async fetchCredits(){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s) return demoCreditBalance("free");
      const u = ls.get("bd_users", {})[s.email] || {};
      const p = u.plan || "free";
      if (!u.credits_initialized && bdPlanCreditAllowance(p) > 0){
        u.plan_credits = bdPlanCreditAllowance(p);
        u.topup_credits = u.topup_credits || 0;
        u.credits_initialized = true;
        u.credits_reset_at = new Date(Date.now() + 30 * 864e5).toISOString();
        u.credits_ledger = [{ kind: "grant", delta: u.plan_credits, note: "Monthly plan credits", at: new Date().toISOString() }];
        ls.set("bd_users", { ...ls.get("bd_users", {}), [s.email]: u });
      }
      return demoCreditBalance(p, u);
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "credits.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "balance" })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Could not load credits.");
    return data;
  },
  async paystackInitTopup(bundleId){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s) throw new Error("Sign in first.");
      const bundle = bdCreditTopup(bundleId);
      if (!bundle) throw new Error("Unknown bundle.");
      const users = ls.get("bd_users", {});
      const u = users[s.email] || {};
      u.topup_credits = (u.topup_credits || 0) + bundle.credits;
      u.credits_ledger = [{ kind: "topup", delta: bundle.credits, note: "Demo top-up", at: new Date().toISOString() }, ...(u.credits_ledger || [])].slice(0, 40);
      users[s.email] = u;
      ls.set("bd_users", users);
      return { demo: true, balance: demoCreditBalance(u.plan || "free", u) };
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "paystack.php" /* brand-ok */, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "init_topup", bundle_id: bundleId })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Could not start checkout.");
    return data;
  },
  async paystackInitSubscribe(plan, interval = "monthly"){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s) throw new Error("Sign in first.");
      const key = bdNormalizePlan(plan);
      const users = ls.get("bd_users", {});
      const u = users[s.email] || {};
      u.plan = key;
      u.plan_credits = bdPlanCreditAllowance(key);
      u.credits_initialized = true;
      u.credits_reset_at = new Date(Date.now() + (interval === "annual" ? 365 : 30) * 864e5).toISOString();
      u.sub_interval = interval === "annual" ? "annual" : "monthly";
      u.sub_status = "active";
      u.card = u.card || { brand: "Visa", last4: "4242" };
      const subPrice = (typeof bdSubPricing === "function") ? bdSubPricing(key, u.sub_interval) : { zar: 0 };
      u.credits_ledger = [{ kind: "grant", delta: bdPlanCreditAllowance(key), note: bdPlanDisplayLabel(key) + " plan · monthly credits", at: new Date().toISOString() }, ...(u.credits_ledger || [])].slice(0, 40);
      users[s.email] = u;
      ls.set("bd_users", users);
      return { demo: true, balance: demoCreditBalance(key, u) };
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "paystack.php" /* brand-ok */, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "init_subscribe", plan, interval })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Could not start subscription.");
    return data;
  },
  async paystackVerify(reference){
    if (DEMO){
      const s = ls.get("bd_session", null);
      const u = s ? ls.get("bd_users", {})[s.email] : null;
      return { ok: true, balance: demoCreditBalance(u?.plan || "free", u) };
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "paystack.php" /* brand-ok */, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "verify", reference })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Payment verification failed.");
    return data;
  },
  async cancelSubscription(){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s) throw new Error("Sign in first.");
      const users = ls.get("bd_users", {});
      const u = users[s.email] || {};
      u.sub_status = "non-renewing";
      u.credits_ledger = [{ kind: "spend", delta: 0, note: "Auto-renew turned off", at: new Date().toISOString() }, ...(u.credits_ledger || [])].slice(0, 40);
      users[s.email] = u;
      ls.set("bd_users", users);
      return { ok: true, balance: demoCreditBalance(u.plan || "free", u) };
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "paystack.php" /* brand-ok */, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "cancel" })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Could not cancel your subscription.");
    return data;
  },
  async resumeSubscription(){
    if (DEMO){
      const s = ls.get("bd_session", null);
      if (!s) throw new Error("Sign in first.");
      const users = ls.get("bd_users", {});
      const u = users[s.email] || {};
      u.sub_status = "active";
      u.credits_ledger = [{ kind: "grant", delta: 0, note: "Auto-renew turned back on", at: new Date().toISOString() }, ...(u.credits_ledger || [])].slice(0, 40);
      users[s.email] = u;
      ls.set("bd_users", users);
      return { ok: true, balance: demoCreditBalance(u.plan || "free", u) };
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "paystack.php" /* brand-ok */, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "resume" })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Could not resume your subscription.");
    return data;
  },
  async updatePaymentMethod(){
    if (DEMO) return { demo: true };
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "paystack.php" /* brand-ok */, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "manage_link" })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Could not open card management.");
    return data;
  },

  /* ----------------------------- visit stats ----------------------------- */
  _visitKey(projectId){
    return "bd_visits_" + this._owner() + "_" + projectId;
  },
  _emptyVisitStats(enabled = false){
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setUTCHours(0, 0, 0, 0);
      d.setUTCDate(d.getUTCDate() - i);
      days.push({
        day: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString(undefined, { weekday: "short" }),
        views: 0,
      });
    }
    return { enabled, total: 0, period_views: 0, days, top_paths: [], updated_at: null };
  },
  async getVisitStats(projectId){
    if (DEMO){
      const store = ls.get(this._visitKey(projectId), null);
      if (!store) return this._emptyVisitStats(false);
      const days = [];
      let period = 0;
      const pathTotals = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        d.setUTCDate(d.getUTCDate() - i);
        const key = d.toISOString().slice(0, 10);
        const bucket = store.days?.[key] || { views: 0, paths: {} };
        const views = bucket.views || 0;
        period += views;
        days.push({
          day: key,
          label: d.toLocaleDateString(undefined, { weekday: "short" }),
          views,
        });
        Object.entries(bucket.paths || {}).forEach(([path, n]) => {
          pathTotals[path] = (pathTotals[path] || 0) + n;
        });
      }
      const top = Object.entries(pathTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([path, views]) => ({ path, views }));
      return {
        enabled: !!store.enabled,
        total: store.total || 0,
        period_views: period,
        days,
        top_paths: top,
        updated_at: store.updated_at || null,
      };
    }
    const token = await this.accessToken();
    const res = await bdFetch(API_BASE + "analytics.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "stats", project_id: projectId })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Could not load visit stats.");
    return data;
  },
  /* Demo helper: seed a visit after enabling analytics so Console isn't empty forever. */
  recordDemoVisit(projectId, path = "/"){
    if (!DEMO) return;
    const key = this._visitKey(projectId);
    const store = ls.get(key, { enabled: true, total: 0, days: {}, updated_at: null });
    store.enabled = true;
    const day = new Date().toISOString().slice(0, 10);
    if (!store.days[day]) store.days[day] = { views: 0, paths: {} };
    store.days[day].views += 1;
    store.days[day].paths[path] = (store.days[day].paths[path] || 0) + 1;
    store.total = (store.total || 0) + 1;
    store.updated_at = new Date().toISOString();
    ls.set(key, store);
  },

  getSupabase(){ return sb; }
};

function demoCreditBalance(planKey, u = {}){
  let allowance = bdPlanCreditAllowance(planKey);
  let planCredits = Math.max(0, u.plan_credits ?? allowance);
  const topupCredits = Math.max(0, u.topup_credits || 0);
  let isPaid = planKey !== "free" && planKey !== "draft";
  let subStatus = isPaid ? (u.sub_status || "active") : "none";
  const endsAt = subStatus === "non-renewing" ? (u.credits_reset_at || null) : null;
  if (subStatus === "non-renewing" && endsAt && Date.parse(endsAt) <= Date.now()) {
    planKey = "free";
    allowance = bdPlanCreditAllowance(planKey);
    planCredits = 0;
    isPaid = false;
    subStatus = "none";
  }
  const interval = u.sub_interval || "monthly";
  const pricing = (typeof bdSubPricing === "function" && isPaid) ? bdSubPricing(planKey, interval) : { zar: 0, per: "month" };
  const chargeFailed = isPaid && (subStatus === "attention" || u.last_charge_failed === true);
  return {
    plan: planKey,
    plan_label: bdPlanDisplayLabel(planKey),
    monthly_allowance: allowance,
    plan_credits: planCredits,
    topup_credits: topupCredits,
    total: planCredits + topupCredits,
    reset_at: u.credits_reset_at || null,
    has_subscription: isPaid,
    subscription: {
      active: isPaid && subStatus === "active",
      status: subStatus,                       // active | non-renewing | attention | none
      interval,                                // monthly | annual
      amount_zar: isPaid ? pricing.zar : 0,
      per: pricing.per,
      next_charge_at: isPaid && subStatus === "active" ? (u.credits_reset_at || null) : null,
      ends_at: subStatus === "non-renewing" ? (u.credits_reset_at || null) : null,
      last_charge_failed: chargeFailed,
      card: isPaid ? (u.card || { brand: "Visa", last4: "4242" }) : null
    },
    ledger: u.credits_ledger || []
  };
}
